import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  TextField,
} from '@mui/material';
import { RocketLaunch as BoostIcon, Refresh as RefreshIcon, Cancel as CancelIcon, Edit as EditIcon, DoNotDisturb as RejectIcon } from '@mui/icons-material';
import ApiService from '../../services/api';
import { isBoostActive } from '../../utils/boost';

const BoostRequests = () => {
  const [tab, setTab] = useState(0); // 0: Pending(requested), 1: Approved(approved), 2: Rejected(rejected)
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expiresMap, setExpiresMap] = useState({});
  const [systemSettings, setSystemSettings] = useState(null);
  const [paymentMetaMap, setPaymentMetaMap] = useState({}); // { userId: { durationDays, amount } }

  const statusForTab = useMemo(() => (tab === 0 ? 'requested' : tab === 1 ? 'approved' : 'rejected'), [tab]);

  const loadBoostUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchLimit = 1000; // request a larger batch for admin view
      // Fetch full role lists then filter client-side to include legacy boosted items
      const [employers, seekers] = await Promise.all([
        ApiService.request(`/users/role/employer?limit=${fetchLimit}`),
        ApiService.request(`/users/role/job_seeker?limit=${fetchLimit}`),
      ]);
      const eAll = employers?.users || employers?.data?.users || [];
      const sAll = seekers?.users || seekers?.data?.users || [];

      // Tab-aware filtering: include legacy boosted users missing boostStatus
      const filterByTab = (u) => {
        if (statusForTab === 'requested') return u.boostStatus === 'requested';
        if (statusForTab === 'approved') return (u.boostStatus === 'approved') || isBoostActive(u.boosted, u.boostExpiresAt);
        if (statusForTab === 'rejected') return u.boostStatus === 'rejected';
        return false;
      };

      const eUsers = eAll.filter(filterByTab).map(u => ({ ...u, role: 'employer' }));
      const sUsers = sAll.filter(filterByTab).map(u => ({ ...u, role: 'job_seeker' }));
      const combined = [...eUsers, ...sUsers];
      const sorted = combined.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setRows(sorted);
      // Load payment metadata (days, amount) for pending requests
      const requirePayment = !!systemSettings?.payments?.enableBoostPayments;
      if (statusForTab === 'requested' && requirePayment) {
        const metaEntries = await Promise.all(sorted.map(async (u) => {
          try {
            const txRes = await ApiService.request(`/payments/transactions/user/${encodeURIComponent(u._id)}`);
            const txList = txRes?.transactions || txRes?.data?.transactions || txRes?.data || [];
            const paid = (Array.isArray(txList) ? txList : []).find((t) => t?.type === 'profile_boost' && t?.status === 'completed');
            if (paid) {
              return [u._id, { durationDays: paid.durationDays || paid?.metadata?.durationDays || null, amount: paid.amount }];
            }
          } catch (_) {}
          return [u._id, { durationDays: null, amount: null }];
        }));
        setPaymentMetaMap(Object.fromEntries(metaEntries));
      } else {
        setPaymentMetaMap({});
      }
    } catch (err) {
      console.error('Failed to load boost users', err);
      setError(err?.message || 'Failed to load boost users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBoostUsers();
    // Load system settings to determine payment gating for approvals
    (async () => {
      try {
        const settingsRes = await ApiService.getSystemSettings();
        setSystemSettings(settingsRes?.data || settingsRes);
      } catch (e) {
        console.error('Failed to load system settings for approvals', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusForTab]);

  const handleApprove = async (user) => {
    try {
      const explicitExpiry = expiresMap[user._id];
      const requirePayment = !!systemSettings?.payments?.enableBoostPayments;
      const payload = {};
      if (explicitExpiry) {
        payload.expiresAt = explicitExpiry;
      }
      if (requirePayment) {
        // Find latest completed profile boost payment for this user
        const txRes = await ApiService.request(`/payments/transactions/user/${encodeURIComponent(user._id)}`);
        const txList = txRes?.transactions || txRes?.data?.transactions || txRes?.data || [];
        const paid = (Array.isArray(txList) ? txList : []).find((t) => t?.type === 'profile_boost' && t?.status === 'completed');
        if (!paid) throw new Error('Valid completed payment is required to boost profile');
        payload.paymentTransactionId = paid.transactionId || paid.id || paid._id;
        // Include requested durationDays to compute expiry server-side when expiresAt not provided
        payload.durationDays = (paid.durationDays || paid?.metadata?.durationDays) ? Number(paid.durationDays || paid?.metadata?.durationDays) : undefined;
      } else {
        // When payment is not required, admin can approve without payment
        payload.override = true;
        if (!explicitExpiry) {
          // Default to 30 days if admin didn't set expiry and payment not required
          payload.durationDays = 30;
        }
      }
      await ApiService.boostUserProfile(user._id, payload);
      setSnackbar({ open: true, message: 'Boost approved', severity: 'success' });
      loadBoostUsers();
    } catch (err) {
      console.error('Failed to approve boost', err);
      setSnackbar({ open: true, message: err?.message || 'Failed to approve boost', severity: 'error' });
    }
  };

  const handleCancel = async (user) => {
    try {
      await ApiService.cancelUserBoost(user._id);
      setSnackbar({ open: true, message: 'Boost canceled', severity: 'success' });
      loadBoostUsers();
    } catch (err) {
      console.error('Failed to cancel boost', err);
      setSnackbar({ open: true, message: err?.message || 'Failed to cancel boost', severity: 'error' });
    }
  };

  const handleEdit = async (user) => {
    try {
      const expiresAt = expiresMap[user._id];
      if (!expiresAt) throw new Error('Please set new expiry date');
      await ApiService.editUserBoost(user._id, { expiresAt });
      setSnackbar({ open: true, message: 'Boost updated', severity: 'success' });
      loadBoostUsers();
    } catch (err) {
      console.error('Failed to edit boost', err);
      setSnackbar({ open: true, message: err?.message || 'Failed to edit boost', severity: 'error' });
    }
  };

  const handleReject = async (user) => {
    try {
      await ApiService.rejectUserBoost(user._id);
      setSnackbar({ open: true, message: 'Boost request rejected', severity: 'success' });
      loadBoostUsers();
    } catch (err) {
      console.error('Failed to reject boost', err);
      setSnackbar({ open: true, message: err?.message || 'Failed to reject boost', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Boost Requests</Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadBoostUsers} disabled={loading}>
          Refresh
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Pending" />
        <Tab label="Approved" />
        <Tab label="Rejected" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              {statusForTab === 'requested' && (
                <>
                  <TableCell>Requested Days</TableCell>
                  <TableCell>Amount</TableCell>
                </>
              )}
              <TableCell>Boost Expires</TableCell>
              <TableCell>Set Expiry</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No users found for this tab.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((u) => (
                <TableRow key={u._id} hover>
                  <TableCell>{u.companyName || u.fullName || u.email}</TableCell>
                  <TableCell>
                    <Chip label={u.role} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip label={u.boostStatus || (u.boosted ? 'approved' : 'none')} color={(u.boostStatus === 'requested') ? 'warning' : (u.boostStatus === 'approved' || u.boosted ? 'success' : (u.boostStatus === 'rejected' ? 'error' : 'default'))} size="small" />
                  </TableCell>
                  {statusForTab === 'requested' && (
                    <>
                      <TableCell>{paymentMetaMap[u._id]?.durationDays ? `${paymentMetaMap[u._id]?.durationDays} day(s)` : '-'}</TableCell>
                      <TableCell>{paymentMetaMap[u._id]?.amount != null ? paymentMetaMap[u._id]?.amount : '-'}</TableCell>
                    </>
                  )}
                  <TableCell>{u.boostExpiresAt ? new Date(u.boostExpiresAt).toLocaleString() : '-'}</TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      size="small"
                      value={(expiresMap[u._id] || '').slice(0, 10)}
                      onChange={(e) => {
                        const date = e.target.value ? new Date(e.target.value).toISOString() : '';
                        setExpiresMap(prev => ({ ...prev, [u._id]: date }));
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {statusForTab === 'requested' && (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button variant="contained" color="secondary" size="small" startIcon={<BoostIcon />} onClick={() => handleApprove(u)}>Approve</Button>
                        <Button variant="outlined" color="error" size="small" startIcon={<RejectIcon />} onClick={() => handleReject(u)}>Reject</Button>
                      </Box>
                    )}
                    {statusForTab === 'approved' && (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button variant="outlined" color="error" size="small" startIcon={<CancelIcon />} onClick={() => handleCancel(u)}>Cancel</Button>
                        <Button variant="contained" color="primary" size="small" startIcon={<EditIcon />} onClick={() => handleEdit(u)}>Edit</Button>
                      </Box>
                    )}
                    {statusForTab === 'rejected' && (
                      <Typography variant="body2" color="text.secondary" align="right">No actions</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BoostRequests;